import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Heading, Text } from '../../common/Typography';
import { Card } from '../../common/Card';
import Button from '../../common/Button';
import { StatusBadge, EmptyState } from '../../common';
import PaymentAliasHintsEditor from '../../payments/PaymentAliasHintsEditor';
import { getGenderTextColor } from '../../../utils/genderColors';

const getPlayerInitials = (firstName, lastName) => {
  const first = (firstName || '').trim().charAt(0).toUpperCase();
  const last = (lastName || '').trim().charAt(0).toUpperCase();
  return `${first}${last}` || '?';
};

const getPlayerAvatarClasses = (gender) => {
  switch (gender?.toLowerCase()) {
    case 'female':
      return 'bg-female/15 text-female border-female/25';
    case 'male':
      return 'bg-primary/10 text-primary border-primary/20';
    default:
      return 'bg-surface-subtle text-text-secondary border-border-medium';
  }
};

const getPlayerRowAccent = (sp, isNoShow, isMarkedNoShow) => {
  if (isMarkedNoShow) return 'border-l-danger';
  if (sp.session_payment === 'paid') return 'border-l-success';
  if (isNoShow) return 'border-l-warning';
  return 'border-l-border-medium';
};

const getPlayerRowState = (sp, session) => {
  const isMarkedNoShow = sp.exit_reason === 'no_show';
  const hasPlayed = (sp.games_played ?? sp.effective_games_played ?? 0) > 0;
  const isNoShow = isMarkedNoShow || !hasPlayed;
  const isLedgerPaid =
    sp.paymentSource === 'lump_payment' || sp.paymentSource === 'legacy_migration';
  const isManualPaid = sp.paymentSource === 'manual_session_toggle';
  const hasPrice = !!session?.price_per_player;

  return {
    fullName: `${sp.players?.first_name || ''} ${sp.players?.last_name || ''}`.trim() || 'Unknown player',
    firstName: sp.players?.first_name || '',
    lastName: sp.players?.last_name || '',
    gender: sp.players?.gender,
    gamesPlayed: sp.games_played ?? sp.effective_games_played ?? 0,
    isMarkedNoShow,
    isNoShow,
    isLedgerPaid,
    isManualPaid,
    hasPrice,
    playerUuid: sp.players?.id,
  };
};

const PaymentStatusBadge = ({ sp, isManualPaid, isLedgerPaid }) => {
  if (sp.session_payment !== 'paid') {
    return (
      <StatusBadge variant="inactive" size="small">
        Not paid
      </StatusBadge>
    );
  }

  let label = 'Paid';
  if (isManualPaid) label = 'Paid here';
  if (isLedgerPaid) {
    label =
      sp.payerId && sp.player_id !== sp.payerId
        ? `Paid by ${[sp.payerFirstName, sp.payerLastName].filter(Boolean).join(' ') || 'someone'}`
        : 'Paid in ledger';
  }

  return (
    <StatusBadge variant={isManualPaid ? 'success' : 'info'} size="small">
      {label}
    </StatusBadge>
  );
};

const AttendanceControl = ({ sp, isAdmin, isMarkedNoShow, isNoShow, mutating, onToggleNoShow }) => {
  const variant = isMarkedNoShow ? 'danger' : isNoShow ? 'warning' : 'success';
  const label = isMarkedNoShow ? 'No-show' : isNoShow ? 'No show?' : 'Attended';

  if (!isAdmin) {
    return (
      <StatusBadge variant={variant} size="small">
        {label}
      </StatusBadge>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onToggleNoShow(sp)}
      disabled={mutating}
      title={isMarkedNoShow || isNoShow ? 'Undo no-show' : 'Mark no-show'}
      className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
    >
      <StatusBadge variant={variant} size="small">
        {label}
      </StatusBadge>
    </button>
  );
};

const PaymentActions = ({
  sp,
  clubId,
  hasPrice,
  isLedgerPaid,
  mutating,
  onTogglePayment,
  onRecordPayment,
}) => {
  if (isLedgerPaid) {
    if (!sp.paymentId) return <span className="text-xs text-text-muted">—</span>;
    return (
      <Link
        to={`/club/${clubId}/payment-tracking?tab=ledger&highlightPayment=${sp.paymentId}`}
        className="text-xs font-medium text-primary hover:text-primary-dark hover:underline"
      >
        View in ledger
      </Link>
    );
  }

  if (!hasPrice) {
    return <span className="text-xs text-text-muted">—</span>;
  }

  if (sp.session_payment === 'paid') {
    return (
      <Button
        size="small"
        variant="outline"
        className="text-xs"
        title="Remove paid status for this session"
        onClick={() => onTogglePayment(sp)}
        disabled={mutating}
      >
        Mark unpaid
      </Button>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      <Button
        size="small"
        variant="primary"
        className="text-xs"
        onClick={() =>
          onRecordPayment({
            id: sp.players?.id,
            first_name: sp.players?.first_name,
            last_name: sp.players?.last_name,
          })
        }
        disabled={mutating}
      >
        Record
      </Button>
      <Button
        size="small"
        variant="outline"
        className="text-xs"
        onClick={() => onTogglePayment(sp)}
        disabled={mutating}
      >
        Mark paid
      </Button>
    </div>
  );
};

const SessionWrapUpPaymentsPanel = ({
  session,
  sessionPlayers,
  selectedFilterPlayer,
  playersLoading,
  playersError,
  isAdmin,
  mutating,
  clubId,
  paymentAliasExpandedPlayerId,
  setPaymentAliasExpandedPlayerId,
  onTogglePayment,
  onToggleNoShow,
  onRecordPayment,
  onMarkAllPaid,
  onMarkAllUnpaid,
  onAddPrice,
  onAliasesSaved,
}) => {
  const filteredPlayers = useMemo(
    () =>
      selectedFilterPlayer
        ? sessionPlayers.filter((sp) => sp.player_id === selectedFilterPlayer.id)
        : sessionPlayers,
    [sessionPlayers, selectedFilterPlayer]
  );

  const hasPrice = !!session?.price_per_player;

  const summaryLine = useMemo(() => {
    if (!filteredPlayers.length) return '';

    if (!hasPrice) {
      return `${filteredPlayers.length} player${filteredPlayers.length === 1 ? '' : 's'} · Payments not tracked`;
    }

    const unpaid = filteredPlayers.filter(
      (sp) => sp.session_payment !== 'paid' && sp.exit_reason !== 'no_show'
    ).length;
    const noShow = filteredPlayers.filter((sp) => sp.exit_reason === 'no_show').length;
    const paid = filteredPlayers.filter((sp) => sp.session_payment === 'paid').length;
    const parts = [`${filteredPlayers.length} player${filteredPlayers.length === 1 ? '' : 's'}`];
    if (paid) parts.push(`${paid} paid`);
    if (unpaid) parts.push(`${unpaid} unpaid`);
    if (noShow) parts.push(`${noShow} no-show`);
    return parts.join(' · ');
  }, [filteredPlayers, hasPrice]);

  const allPaid =
    sessionPlayers.length > 0 && sessionPlayers.every((sp) => sp.session_payment === 'paid');

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Heading level={3} className="mb-0">
            Payments & attendance
          </Heading>
          {filteredPlayers.length > 0 ? (
            <Text className="mt-1 text-xs text-text-secondary">{summaryLine}</Text>
          ) : null}
        </div>
        {sessionPlayers.length > 0 && hasPrice ? (
          <Button
            size="small"
            variant="outline"
            onClick={allPaid ? onMarkAllUnpaid : onMarkAllPaid}
            disabled={mutating}
          >
            {allPaid ? 'Mark all unpaid' : 'Mark all paid'}
          </Button>
        ) : null}
      </div>

      {playersLoading ? (
        <div className="py-8 text-center text-text-muted">Loading players…</div>
      ) : playersError ? (
        <div className="py-8 text-center text-danger">{playersError}</div>
      ) : !hasPrice ? (
        <Card
          variant="default"
          size="medium"
          className="border-l-4 border-l-info bg-info-light/10 !p-6 sm:!p-8"
        >
          <EmptyState
            className="py-4"
            title="Payments not tracked for this session"
            description="No per-player price — archive without marking payments."
            action={
              isAdmin ? (
                <Button variant="outline" size="medium" onClick={onAddPrice}>
                  Add session price
                </Button>
              ) : null
            }
          />
        </Card>
      ) : filteredPlayers.length === 0 ? (
        <div className="py-8 text-center text-text-muted">
          {selectedFilterPlayer
            ? 'No players found matching the filter.'
            : 'No players found for this session.'}
        </div>
      ) : (
        <>
          {isAdmin ? (
            <Text className="mb-3 text-xs text-text-muted">
              Tap attendance to toggle no-show. Use Record for ledger payments or Mark paid for a
              quick session mark.
            </Text>
          ) : null}

          <div
            className="mb-2 hidden gap-4 px-3 text-xs font-medium uppercase tracking-wide text-text-muted sm:grid sm:grid-cols-[minmax(0,1fr)_7.5rem_7.5rem_8.5rem]"
            aria-hidden
          >
            <span>Player</span>
            <span>Payment</span>
            <span>Attendance</span>
            <span className="text-right">Actions</span>
          </div>

          <div className="space-y-2">
            {filteredPlayers.map((sp) => {
              const state = getPlayerRowState(sp, session);
              const aliasRowOpen =
                isAdmin && paymentAliasExpandedPlayerId === sp.player_id && state.playerUuid;
              const rowAccent = getPlayerRowAccent(sp, state.isNoShow, state.isMarkedNoShow);

              return (
                <div
                  key={sp.id}
                  className={`overflow-hidden rounded-xl border border-border-medium border-l-4 bg-surface shadow-brand-sm transition-colors hover:bg-surface-subtle/40 ${rowAccent}`}
                >
                  <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-[minmax(0,1fr)_7.5rem_7.5rem_8.5rem] sm:items-center sm:gap-4 sm:p-3.5">
                    <div className="flex min-w-0 items-center gap-3">
                      <div
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-xs font-bold ${getPlayerAvatarClasses(state.gender)}`}
                        aria-hidden
                      >
                        {getPlayerInitials(state.firstName, state.lastName)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span
                            className={`truncate text-sm font-semibold ${getGenderTextColor(state.gender)}`}
                          >
                            {state.fullName}
                          </span>
                          {isAdmin && state.playerUuid ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="small"
                              className="h-7 shrink-0 px-2 text-xs"
                              onClick={() =>
                                setPaymentAliasExpandedPlayerId((cur) =>
                                  cur === sp.player_id ? null : sp.player_id
                                )
                              }
                              aria-expanded={aliasRowOpen}
                            >
                              {aliasRowOpen ? 'Hide hints' : 'Hints'}
                            </Button>
                          ) : null}
                        </div>
                        <Text className="mt-0.5 text-xs text-text-muted">
                          {state.gamesPlayed} game{state.gamesPlayed === 1 ? '' : 's'}
                          {sp.ledgerPaymentDate && sp.session_payment === 'paid'
                            ? ` · Paid ${new Date(sp.ledgerPaymentDate).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                              })}`
                            : ''}
                        </Text>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-2 sm:block">
                      <span className="text-xs font-medium text-text-muted sm:sr-only">Payment</span>
                      <PaymentStatusBadge
                        sp={sp}
                        isManualPaid={state.isManualPaid}
                        isLedgerPaid={state.isLedgerPaid}
                      />
                    </div>

                    <div className="flex items-center justify-between gap-2 sm:block">
                      <span className="text-xs font-medium text-text-muted sm:sr-only">Attendance</span>
                      <AttendanceControl
                        sp={sp}
                        isAdmin={isAdmin}
                        isMarkedNoShow={state.isMarkedNoShow}
                        isNoShow={state.isNoShow}
                        mutating={mutating}
                        onToggleNoShow={onToggleNoShow}
                      />
                    </div>

                    <div className="flex items-center justify-between gap-2 sm:justify-end">
                      <span className="text-xs font-medium text-text-muted sm:sr-only">Actions</span>
                      <PaymentActions
                        sp={sp}
                        clubId={clubId}
                        hasPrice={state.hasPrice}
                        isLedgerPaid={state.isLedgerPaid}
                        mutating={mutating}
                        onTogglePayment={onTogglePayment}
                        onRecordPayment={onRecordPayment}
                      />
                    </div>
                  </div>

                  {aliasRowOpen ? (
                    <div className="border-t border-border-light bg-surface-subtle/30 px-3 py-3 sm:px-4">
                      <PaymentAliasHintsEditor
                        key={state.playerUuid}
                        clubId={clubId}
                        playerId={state.playerUuid}
                        initialAliases={sp.payment_search_aliases || []}
                        compact
                        onSaved={(aliases) => onAliasesSaved(sp, state.playerUuid, aliases)}
                      />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
};

export default SessionWrapUpPaymentsPanel;
